// AstriaJapanV2Shell.tsx
import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Share2, Star, Heart, Sparkles } from "lucide-react";
import { useJapanLanguage } from "./japanLanguage";
import { L, LC, useLnLanguage } from "../../utils/lnText";

// ─── Astria Japan V2 — shared shell (header + Share button + tab bar) ───────
// Wraps the Viral (Kyusei) / Timing / Companion result screens in one consistent card,
// mirroring the structure of AstriaJapanV3Shell.tsx but with a distinct warm
// sakura/coral palette so v2 reads as its own visual lane.

export const JP2_FONT = "'Noto Sans JP', sans-serif";

export const SHELL_COLORS_V2 = {
  bg: "linear-gradient(160deg, #FFFDFB 0%, #FBF3EF 100%)",
  card: "#FFFFFF",
  section: "#FBF3EF",
  cardBorder: "#F3E4DC",
  titleColor: "#2B211D",
  sectionTitleColor: "#3A2C25",
  bodyColor: "#4A3B33",
  mutedColor: "#8A7469",
  accent: "#D4735D",
  accentSoft: "rgba(212,115,93,0.12)",
};

export type AstriaJapanV2Tab = "viral" | "timing" | "companion";

// ─── Tab Configuration ─────────────────────────────────────────────────────────
// 3 tabs: Viral (Kyusei/Omamori), Timing (Heart Flow), Companion (Inner Space)
// Each tab has: id, label (JA/EN), icon, path

const TABS: {
  key: AstriaJapanV2Tab;
  label: { ja: string; en: string };
  icon: React.ReactNode;
  path: string;
}[] = [
  {
    key: "viral",
    label: { ja: "九星とお守り", en: "Kyusei & Omamori" },
    icon: <Star className="w-4 h-4" />,
    path: "/astria-japan/kyusei/result",
  },
  {
    key: "timing",
    label: { ja: "心の流れ", en: "Heart Flow" },
    icon: <Heart className="w-4 h-4" />,
    path: "/astria-japan/timing/result",
  },
  {
    key: "companion",
    label: { ja: "インナー・スペース", en: "Inner Space" },
    icon: <Sparkles className="w-4 h-4" />,
    path: "/astria-japan/companion/result",
  },
];

interface AstriaJapanV2ShellProps {
  active: AstriaJapanV2Tab;
  onShare?: () => void;
  sharing?: boolean;
  children: React.ReactNode;
}

const AstriaJapanV2Shell: React.FC<AstriaJapanV2ShellProps> = ({
  active,
  onShare,
  sharing,
  children,
}) => {
  useLnLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const language = useJapanLanguage();

  // ─── Tab Change Handler ──────────────────────────────────────────────────────
  const handleTabChange = (tabKey: AstriaJapanV2Tab) => {
    const tab = TABS.find((t) => t.key === tabKey);
    if (tab && tab.path !== location.pathname) {
      navigate(tab.path);
    }
  };

  return (
    <div
      className="min-h-screen px-4 py-10"
      style={{ background: SHELL_COLORS_V2.bg, fontFamily: JP2_FONT }}
    >
      <div
        className="mx-auto"
        style={{
          background: SHELL_COLORS_V2.card,
          borderRadius: 20,
          boxShadow: "0 8px 32px rgba(212,115,93,0.10)",
          maxWidth: 720,
          padding: 32,
        }}
      >
        {/* ─── Header ────────────────────────────────────────────────────────── */}
        <div
          className="flex items-start justify-between gap-4"
          style={{ marginBottom: 24 }}
        >
          <div>
            <div
              style={{
                fontSize: 20,
                fontWeight: 500,
                color: SHELL_COLORS_V2.titleColor,
                marginBottom: 4,
                lineHeight: 1.3,
                letterSpacing: "0.02em",
              }}
            >
              {language === "ja" ? "Astria Japan" : L("Astria Japan")}
            </div>
            <p
              style={{
                fontSize: 12,
                color: SHELL_COLORS_V2.mutedColor,
                lineHeight: 1.6,
                letterSpacing: "0.03em",
              }}
            >
              {language === "ja"
                ? "あなたの心に、そっと寄り添う時間を。"
                : L("A gentle moment for your heart.")}
            </p>
          </div>
          {onShare && (
            <button
              onClick={onShare}
              disabled={sharing}
              className="flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium border disabled:opacity-60 flex-shrink-0 transition-all hover:bg-gray-50"
              style={{
                borderColor: SHELL_COLORS_V2.cardBorder,
                color: SHELL_COLORS_V2.bodyColor,
                background: "transparent",
              }}
            >
              <Share2 className="w-3.5 h-3.5" />
              {language === "ja" ? "シェア" : L("Share")}
            </button>
          )}
        </div>

        {/* ─── Tab Bar ────────────────────────────────────────────────────────── */}
        <div
          className="flex gap-1 overflow-x-auto"
          style={{
            marginBottom: 28,
            background: "#F5F0EC",
            borderRadius: 12,
            padding: 4,
          }}
        >
          {TABS.map((tab) => {
            const isActive = tab.key === active;
            const label = LC(tab.label, language);

            return (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className="flex items-center justify-center gap-2 flex-1 whitespace-nowrap rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200"
                style={{
                  background: isActive ? "#FFFFFF" : "transparent",
                  color: isActive
                    ? SHELL_COLORS_V2.accent
                    : SHELL_COLORS_V2.mutedColor,
                  boxShadow: isActive ? "0 2px 8px rgba(0,0,0,0.06)" : "none",
                  fontWeight: isActive ? 600 : 500,
                }}
              >
                <span
                  style={{
                    opacity: isActive ? 1 : 0.6,
                    transition: "opacity 0.2s",
                  }}
                >
                  {tab.icon}
                </span>
                <span>{label}</span>
              </button>
            );
          })}
        </div>

        {/* ─── Content ────────────────────────────────────────────────────────── */}
        <div className="relative">{children}</div>
      </div>
    </div>
  );
};

export default AstriaJapanV2Shell;
